/* Del Mesa Liquor — Shipping Protection (in-house replacement for the Simply Insurance app)
   Sync engine + widget renderer.
   - Auto-adds the correct Shipping Protection tier variant to the cart (default ON).
   - Opt-out via cookie 'dm_sp_optout=1' (1-day expiry, mirrors the old app's semantics).
   - Respects a customer's manual removal of the protection line item (treated as opt-out).
   - Loop-safe: own requests carry header 'X-DM-SP': '1' and are ignored by the interceptors;
     an in-flight flag + 250ms debounce guard every re-entry.
*/
(function () {
  'use strict';

  var PROTECTION_PRODUCT_ID = 6606559674411;
  var COOKIE_NAME = 'dm_sp_optout';
  var CART_ATTRIBUTE = 'dm-shipping-protection';

  /* Fixed tiers: eligible subtotal (cents) -> protection variant.
     Boundaries mirror the old Simply plan table exactly. */
  var TIERS = [
    { variantId: 39436655788075, min: 100,   max: 9999,  priceLabel: '$2.99' },
    { variantId: 39439153365035, min: 10000, max: 19999, priceLabel: '$5.99' },
    { variantId: 39439153430571, min: 20000, max: 29999, priceLabel: '$7.99' },
    { variantId: 39439153528875, min: 30000, max: 39999, priceLabel: '$9.99' },
    { variantId: 39439153594411, min: 40000, max: 49999, priceLabel: '$11.99' },
    { variantId: 39439153791019, min: 50000, max: 59999, priceLabel: '$13.99' },
    { variantId: 39439154413611, min: 60000, max: 69999, priceLabel: '$15.99' },
    { variantId: 39439154741291, min: 70000, max: 79999, priceLabel: '$19.99' },
    { variantId: 39439154937899, min: 80000, max: 89999, priceLabel: '$24.99' },
    { variantId: 39439155363883, min: 90000, max: null,  priceLabel: '$29.99' }
  ];
  var TIER_BY_VARIANT = {};
  TIERS.forEach(function (t) { TIER_BY_VARIANT[t.variantId] = t; });

  var state = {
    cart: null,               // last /cart.json snapshot we evaluated
    inFlight: false,          // a sync/update request is in progress
    timer: null,              // debounce timer
    weAddedThisSession: false // we added the protection line during this page session
  };

  /* ------------------------------------------------------------------ cookies */
  function isOptedOut() {
    return document.cookie.split('; ').indexOf(COOKIE_NAME + '=1') !== -1;
  }
  function setOptOut(on) {
    if (on) {
      var d = new Date();
      d.setTime(d.getTime() + 24 * 60 * 60 * 1000); // 1 day, like the old app
      document.cookie = COOKIE_NAME + '=1; expires=' + d.toUTCString() + '; path=/; SameSite=Lax';
    } else {
      document.cookie = COOKIE_NAME + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';
    }
  }

  /* ------------------------------------------------- removal fingerprint
     Records the last synced state of this cart (token + protection variant) so a
     manual line removal that happens across a full page reload (the cart page's
     remove form navigates) is still recognized on the next page session. */
  var STORE_KEY = 'dm_sp_last';
  function readStore() {
    try { return JSON.parse(window.localStorage.getItem(STORE_KEY) || 'null'); } catch (e) { return null; }
  }
  function writeStore(cart) {
    try {
      var lines = protectionLines(cart);
      window.localStorage.setItem(STORE_KEY, JSON.stringify({
        token: cart.token || null,
        variant: lines.length ? lines[0].variant_id : null
      }));
    } catch (e) { /* storage unavailable; in-session detection still applies */ }
  }

  /* ------------------------------------------------------------------ cart math */
  function eligibleSubtotal(cart) {
    var sum = 0;
    (cart.items || []).forEach(function (item) {
      if (item.product_id !== PROTECTION_PRODUCT_ID) sum += item.final_line_price;
    });
    return sum;
  }
  function desiredTier(cart) {
    var subtotal = eligibleSubtotal(cart);
    if (!cart.items || cart.items.length === 0 || subtotal < 100) return null;
    for (var i = 0; i < TIERS.length; i++) {
      var t = TIERS[i];
      if (subtotal >= t.min && (t.max === null || subtotal <= t.max)) return t;
    }
    return null;
  }
  function protectionLines(cart) {
    return (cart.items || []).filter(function (item) {
      return item.product_id === PROTECTION_PRODUCT_ID;
    });
  }

  function fetchCart() {
    return fetch('/cart.json', { credentials: 'same-origin', headers: { 'X-DM-SP': '1' } })
      .then(function (r) { return r.json(); });
  }

  /* ------------------------------------------------------------------ cart writes */
  // Single write path for every cart mutation we make. `updates` maps variantId -> qty.
  function postUpdate(updates, attributeValue) {
    var body = { updates: updates };
    if (attributeValue) {
      body.attributes = {};
      body.attributes[CART_ATTRIBUTE] = attributeValue;
    }
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-DM-SP': '1' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function tellThemeToRebuild() {
    // Impulse listens for 'cart:build' and re-renders drawer/page item markup,
    // subtotal, discounts and the cart count from fresh server markup.
    document.dispatchEvent(new CustomEvent('cart:build'));
  }

  /* ------------------------------------------------------------------ sync engine */
  function scheduleSync(source, delay) {
    if (state.timer) clearTimeout(state.timer);
    state.timer = setTimeout(function () { runSync(source || 'event', false); }, delay == null ? 250 : delay);
  }

  // opts.forceRemove: invoked from the widget toggle being switched OFF (direct user intent).
  function runSync(source, fromToggleOff) {
    if (state.inFlight) { scheduleSync(source, 400); return; }
    fetchCart().then(function (cart) {
      var prevCart = state.cart;
      var optedOut = isOptedOut();

      /* Manual removal across a page reload: the last recorded state of THIS cart
         (same token) had a protection line; now it is gone while a tier is still
         warranted -> the customer removed it outside this page session. Opt out. */
      if (source === 'init' && !optedOut) {
        var stored = readStore();
        if (stored && stored.token && cart.token && stored.token === cart.token &&
            stored.variant && protectionLines(cart).length === 0 && desiredTier(cart)) {
          setOptOut(true);
          optedOut = true;
          writeAttributeOnly('off');
        }
      }

      /* Manual-removal respect: an external change removed the protection line while
         a tier is still warranted -> treat as opt-out instead of re-adding. */
      if (source === 'external' && prevCart) {
        var hadBefore = protectionLines(prevCart).length > 0;
        var hasNow = protectionLines(cart).length > 0;
        if (hadBefore && !hasNow && desiredTier(cart) && !optedOut) {
          setOptOut(true);
          optedOut = true;
          state.weAddedThisSession = false;
          writeAttributeOnly('off');
        }
      }

      state.cart = cart;
      writeStore(cart);
      renderWidgets(cart);

      var tier = desiredTier(cart);
      var lines = protectionLines(cart);
      var currentVariantIds = {};
      lines.forEach(function (l) { currentVariantIds[l.variant_id] = true; });

      if (optedOut) {
        // Only remove what we put there (this page session), or on explicit toggle-off.
        if (lines.length && (state.weAddedThisSession || fromToggleOff)) {
          var offUpdates = {};
          lines.forEach(function (l) { offUpdates[l.variant_id] = 0; });
          state.inFlight = true;
          postUpdate(offUpdates, 'off').then(function () {
            state.weAddedThisSession = false;
            return afterOurUpdate();
          }).catch(endFlight);
        }
        return;
      }

      var desiredId = tier ? tier.variantId : null;
      var needsChange =
        lines.some(function (l) { return l.variant_id !== desiredId || l.quantity !== 1; }) ||
        (desiredId && !currentVariantIds[desiredId]) ||
        (!desiredId && lines.length > 0);

      if (!needsChange) return;

      var updates = {};
      lines.forEach(function (l) { updates[l.variant_id] = 0; });
      if (desiredId) updates[desiredId] = 1;

      state.inFlight = true;
      postUpdate(updates, desiredId ? 'on' : 'off').then(function () {
        if (desiredId) state.weAddedThisSession = true;
        return afterOurUpdate();
      }).catch(endFlight);
    }).catch(function () { /* cart fetch failed; next event will retry */ });
  }

  function writeAttributeOnly(value) {
    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-DM-SP': '1' },
      body: JSON.stringify({ updates: {}, attributes: (function () { var a = {}; a[CART_ATTRIBUTE] = value; return a; })() })
    }).catch(function () {});
  }

  function afterOurUpdate() {
    // Re-fetch once, render, and stop (no retry loop even if state still differs).
    return fetchCart().then(function (cart) {
      state.cart = cart;
      writeStore(cart);
      renderWidgets(cart);
      tellThemeToRebuild();
      state.inFlight = false;
    }).catch(endFlight);
  }
  function endFlight() { state.inFlight = false; }

  /* ------------------------------------------------------------------ widget UI */
  function widgetSignature(cart) {
    var tier = desiredTier(cart);
    return [
      tier ? tier.variantId : 'none',
      isOptedOut() ? 'off' : 'on',
      eligibleSubtotal(cart)
    ].join('|');
  }

  function renderWidgets(cart) {
    var mounts = document.querySelectorAll('[data-dmsp-mount]');
    if (!mounts.length) return;
    var tier = desiredTier(cart);
    var hasEligible = cart && cart.items && cart.items.some(function (i) {
      return i.product_id !== PROTECTION_PRODUCT_ID;
    }) && eligibleSubtotal(cart) >= 100;

    mounts.forEach(function (mount) {
      if (!tier || !hasEligible) {
        if (mount.innerHTML) mount.innerHTML = '';
        mount.setAttribute('hidden', '');
        mount.removeAttribute('data-dmsp-sig');
        return;
      }
      mount.removeAttribute('hidden');
      var sig = widgetSignature(cart);
      if (mount.getAttribute('data-dmsp-sig') === sig && mount.querySelector('.dmsp-widget')) return;
      mount.setAttribute('data-dmsp-sig', sig);

      var on = !isOptedOut();
      mount.innerHTML =
        '<div class="dmsp-widget" role="group" aria-label="Shipping Protection">' +
          '<div class="dmsp-row">' +
            '<div class="dmsp-text">' +
              '<div class="dmsp-title-row">' +
                '<span class="dmsp-title">Shipping Protection</span>' +
                '<span class="dmsp-price">' + tier.priceLabel + '</span>' +
              '</div>' +
              '<p class="dmsp-desc">Get peace of mind with Shipping Protection in the event your delivery is damaged, stolen, or lost during transit.</p>' +
            '</div>' +
            '<label class="dmsp-switch">' +
              '<input type="checkbox" class="dmsp-toggle" aria-label="Shipping Protection"' + (on ? ' checked' : '') + '>' +
              '<span class="dmsp-slider" aria-hidden="true"></span>' +
            '</label>' +
          '</div>' +
        '</div>';

      var toggle = mount.querySelector('.dmsp-toggle');
      toggle.addEventListener('change', function () {
        if (toggle.checked) {
          setOptOut(false);
          scheduleSync('toggle', 0);
        } else {
          setOptOut(true);
          runSync('toggle', true);
        }
      });
    });
  }

  /* ------------------------------------------------------------------ interception */
  function isCartMutation(url) {
    return typeof url === 'string' &&
      (url.indexOf('/cart/add') !== -1 || url.indexOf('/cart/update') !== -1 || url.indexOf('/cart/change') !== -1);
  }
  function headersHaveMarker(headers) {
    if (!headers) return false;
    try {
      if (typeof Headers !== 'undefined' && headers instanceof Headers) return headers.get('X-DM-SP') === '1';
      if (Array.isArray(headers)) return headers.some(function (h) { return String(h[0]).toLowerCase() === 'x-dm-sp'; });
      return headers['X-DM-SP'] === '1' || headers['x-dm-sp'] === '1';
    } catch (e) { return false; }
  }

  function interceptFetch() {
    if (!window.fetch || window.fetch.__dmspWrapped) return;
    var origFetch = window.fetch;
    function wrappedFetch(input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var headers = (init && init.headers) || (input && input.headers);
      var ours = headersHaveMarker(headers);
      return origFetch.apply(this, arguments).then(function (response) {
        if (!ours && isCartMutation(url)) scheduleSync('external');
        return response;
      });
    }
    wrappedFetch.__dmspWrapped = true;
    window.fetch = wrappedFetch;
  }

  function interceptXHR() {
    if (!window.XMLHttpRequest || window.XMLHttpRequest.__dmspWrapped) return;
    var proto = window.XMLHttpRequest.prototype;
    var origOpen = proto.open;
    var origSend = proto.send;
    var origSetHeader = proto.setRequestHeader;
    proto.open = function (method, url) {
      this.__dmspUrl = url;
      this.__dmspOurs = false;
      return origOpen.apply(this, arguments);
    };
    proto.setRequestHeader = function (name, value) {
      if (String(name).toLowerCase() === 'x-dm-sp') this.__dmspOurs = true;
      return origSetHeader.apply(this, arguments);
    };
    proto.send = function () {
      var xhr = this;
      xhr.addEventListener('loadend', function () {
        if (!xhr.__dmspOurs && isCartMutation(xhr.__dmspUrl || '')) scheduleSync('external');
      });
      return origSend.apply(this, arguments);
    };
    window.XMLHttpRequest.__dmspWrapped = true;
  }

  /* ------------------------------------------------------------------ hooks */
  function observeDrawer() {
    var drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    var wasOpen = drawer.classList.contains('drawer--is-open');
    new MutationObserver(function () {
      var open = drawer.classList.contains('drawer--is-open');
      if (open && !wasOpen) scheduleSync('drawer', 50);
      wasOpen = open;
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function observeMounts() {
    // If a theme re-render ever replaces a mount's contents, re-render from last state.
    if (!window.MutationObserver) return;
    var scheduled = false;
    new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      setTimeout(function () {
        scheduled = false;
        var mounts = document.querySelectorAll('[data-dmsp-mount]');
        mounts.forEach(function (mount) {
          if (!mount.querySelector('.dmsp-widget') && !mount.hasAttribute('hidden')) {
            mount.removeAttribute('data-dmsp-sig');
            if (state.cart) renderWidgets(state.cart);
          }
        });
      }, 300);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function start() {
    interceptFetch();
    interceptXHR();
    observeDrawer();
    observeMounts();
    document.addEventListener('cart:updated', function () { scheduleSync('external'); });
    scheduleSync('init', 0);
  }

  // Public handle (used by theme hooks if needed).
  window.DelMesaSP = { refresh: function () { scheduleSync('manual', 0); } };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
